export const PICKUP_STATUS = {
  BOTH: 'both',
  PICKUP_ONLY: 'pickupOnly',
  DROPOFF_ONLY: 'dropoffOnly',
  SCHEDULE_ABSENT: 'scheduleAbsent'
}

export const PICKUP_STATUS_TEXT = {
	both: 'Both',
	pickupOnly: 'Pickup Only',
	dropoffOnly: 'Dropoff Only',
	scheduleAbsent: 'Schedule Absent'
}

export const CUSTOMER_TYPE = {
	MEMBER: 'member',
	VISITOR: 'visitor',
	SELF_PAY: 'self_pay',
	TRANSFERRED: 'transferred',
	DECEASED: 'deceased',
	DISCHARGED: 'discharged'
}

export const CUSTOMER_TYPE_TEXT = {
	member: 'Member',
	visitor: 'Visitor',
	transferred: 'Transferred',
	deceased: 'Deceased',
	discharged: 'Discharged'
}

const normalizedType = (customer) => `${customer?.type || ''}`.trim().toLowerCase();

/**
 * Customer list / dashboard "Active" tab: status active and type is not discharged, transferred, or deceased.
 */
export function isCustomerListActive(customer) {
	if (!customer) return false;
	const st = `${customer.status || ''}`.trim().toLowerCase();
	if (st !== 'active') return false;
	const t = normalizedType(customer);
	if (t === CUSTOMER_TYPE.TRANSFERRED || t === 'transferred') return false;
	if (t === CUSTOMER_TYPE.DECEASED || t === 'deceased') return false;
	if (t === CUSTOMER_TYPE.DISCHARGED || t === 'discharged') return false;
	return true;
}

/**
 * Treated as discharged for medical labels, stats, etc.:
 * legacy `type === discharged`, or inactive while type is not transferred/deceased (member/visitor/etc. kept on discharge).
 */
export function isCustomerDischarged(customer) {
	if (!customer) return false;
	const t = normalizedType(customer);
	if (t === CUSTOMER_TYPE.TRANSFERRED || t === 'transferred') return false;
	if (t === CUSTOMER_TYPE.DECEASED || t === 'deceased') return false;
	if (t === CUSTOMER_TYPE.DISCHARGED || t === 'discharged') return true;
	const st = `${customer.status || ''}`.trim().toLowerCase();
	return st !== 'active';
}

/**
 * Medical event UIs (lists, calendars): append " (Discharged)" when the linked customer is discharged
 * (see {@link isCustomerDischarged}).
 */
export function formatMedicalEventCustomerNameWithDischargeStatus(displayName, customer) {
	const base = `${displayName || ''}`.trim();
	if (!base) return '';
	if (isCustomerDischarged(customer)) {
		return `${base} (Discharged)`;
	}
	return base;
}

/**
 * Medical appointment pickers (create request, create/update event, multi-day filters):
 * same rule as customer list "Active" (status active; type not discharged/transferred/deceased).
 */
export function isCustomerSelectableForMedicalAppointment(customer) {
	return isCustomerListActive(customer);
}

export const PROGRAM_TYPE = {
	AMDC: 'amdc',
	SENIOR_PLUS: 'seniorPlus'
}

export const PROGRAM_TYPE_TEXT = {
	amdc: 'AMDC',
	seniorPlus: 'Senior Plus'
}

export const PAY_SOURCE = {
	PRIVATE_PAY: 'privatePay',
	MEDICAID: 'medicaid',
	QMB: 'qmb',
	VETERAN: 'veteran',
	OTHER: 'other'
}

export const PAY_SOURCE_TEXT = {
	privatePay: 'Private Pay',
	medicaid: 'Medicaid',
	qmb: 'QMB',
	veteran: 'Veteran',
	other: 'Other-Please Specify'
}

export const LEGAL_SEX = {
	FEMALE: 'female',
	MALE: 'male'
}

export const LEGAL_SEX_TEXT = {
	female: 'Female',
	male: 'Male'
}

export const MARITAL_STATUS = {
	MARRIED: 'married',
	WIDOWED: 'widowed',
	SINGLE: 'single',
	DIVORCED: 'divorced'
}

export const MARITAL_STATUS_TEXT = {
	married: 'Married',
	widowed: 'Widowed',
	single: 'Single',
	divorced: 'Divorced'
}

export const IMMIGRATION_STATUS = {
	US_CITIZEN: 'usCitizen',
	GREEN_CARD_HOLDER: 'greenCardHolder',
	PERMANENT_RESIDENT: 'permanentResident',
	OTHER: 'other'
}

export const IMMIGRATION_STATUS_TEXT = {
	usCitizen: 'U.S. Citizen',
	greenCardHolder: 'Green Card Holder',
	permanentResident: 'Permanent Resident',
	other: 'Other-Please Specify'
}

export const LANGUAGE_OPTIONS = [
	{ value: 'english', label: 'English' },
	{ value: 'mandarin', label: 'Mandarin' },
	{ value: 'cantonese', label: 'Cantonese' },
	{ value: 'vietnamese', label: 'Vietnamese' },
	{ value: 'korean', label: 'Korean' },
	{ value: 'thai', label: 'Thai' },
	{ value: 'burmese', label: 'Burmese' },
	{ value: 'french', label: 'French' },
	{ value: 'haitianCreole', label: 'Haitian Creole' },
	{ value: 'hindi', label: 'Hindi' },
	{ value: 'persian', label: 'Persian' },
	{ value: 'spanish', label: 'Spanish' },
	{ value: 'urdu', label: 'Urdu' },
	{ value: 'other', label: 'Other-Please Specify' }
]

export const STATE_OPTIONS = {
	MD: 'MD',
	VA: 'VA'
}

export const STATE_OPTIONS_TEXT = {
	MD: 'MD',
	VA: 'VA',
	// Legacy stored values — still display when present on existing records
	maryland: 'Maryland',
	virginia: 'Virginia'
}

export const EMERGENCY_CONTACT_RELATIONSHIP = {
	SON: 'son',
	DAUGHTER: 'daughter',
	SPOUSE: 'spouse',
	FRIEND: 'friend',
	CAREGIVER: 'caregiver',
	OTHER: 'other'
}

export const EMERGENCY_CONTACT_RELATIONSHIP_TEXT = {
	son: 'Son',
	daughter: 'Daughter',
	spouse: 'Spouse',
	friend: 'Friend',
	caregiver: 'Caregiver',
	other: 'Other-Please Specify'
}

export const EMERGENCY_CONTACT_ROLE_OPTIONS = [
	{ value: 'nextOfKin', label: 'Next of Kin' },
	{ value: 'emergencyContact', label: 'Emergency contact' },
	{ value: 'payor', label: 'Payor' },
	{ value: 'primaryCaregiver', label: 'Primary Caregiver' },
	{ value: 'careProvider', label: 'Care Provider' },
	{ value: 'caseManager', label: 'Case Manager' },
	{ value: 'powerOfAttorney', label: 'Power of Attorney' },
	{ value: 'serviceCoordinator', label: 'Service Coordinator' },
	{ value: 'friend', label: 'Friend' },
	{ value: 'guardianOrPoaForHealthcare', label: 'Guardian or PoA for Healthcare' },
	{ value: 'healthcareProxy', label: 'Healthcare Proxy' }
]

export const DAYS_OF_WEEK_OPTIONS = [
	{ value: 'monday', label: 'Monday' },
	{ value: 'tuesday', label: 'Tuesday' },
	{ value: 'wednesday', label: 'Wednesday' },
	{ value: 'thursday', label: 'Thursday' },
	{ value: 'friday', label: 'Friday' },
	{ value: 'saturday', label: 'Saturday' },
	{ value: 'sunday', label: 'Sunday' }
]

// Referral Source - Single Choice with categories
export const REFERRAL_SOURCE = {
	// Returning
	REJOIN: 'rejoin',
	// Referral
	FAMILY_FRIENDS: 'familyFriends',
	HEALTHCARE_PROVIDER: 'healthcareProvider',
	SOCIAL_WORKER: 'socialWorker',
	SUPPORT_PLANNER: 'supportPlanner',
	// Online & Media Source
	ONLINE_SEARCH: 'onlineSearch',
	FACEBOOK: 'facebook',
	INSTAGRAM: 'instagram',
	REDNOTE: 'rednote',
	YOUTUBE: 'youtube',
	WECHAT: 'wechat',
	NEWSPAPER: 'newspaper',
	NEXTDOOR: 'nextdoor',
	// Event
	EVENT: 'event',
	// N/A
	NA: 'na',
	// Other
	OTHER: 'other'
}

export const REFERRAL_SOURCE_TEXT = {
	// Returning (legacy values kept for display on existing records)
	transferWorldshine: 'Transfer from Another Worldshine Center',
	transferAmdc: 'Transfer from Another AMDC Center',
	rejoin: 'Rejoin',
	// Referral
	familyFriends: 'Family and Friends',
	healthcareProvider: 'Healthcare Provider Referral',
	socialWorker: 'Social Worker Referral',
	supportPlanner: 'Support Planner',
	// Online & Media Source
	onlineSearch: 'Online Search',
	facebook: 'Facebook',
	instagram: 'Instagram',
	rednote: 'RedNote',
	youtube: 'YouTube',
	wechat: 'WeChat Official Account',
	newspaper: 'Newspaper',
	nextdoor: 'Nextdoor',
	// Event
	event: 'Event',
	// N/A
	na: 'N/A',
	// Other
	other: 'Other-Please Specify'
}

// Referral Source grouped options for display
export const REFERRAL_SOURCE_GROUPED = [
	{
		label: 'Referral',
		options: [
			{ value: 'familyFriends', label: 'Family and Friends' },
			{ value: 'healthcareProvider', label: 'Healthcare Provider Referral' },
			{ value: 'socialWorker', label: 'Social Worker Referral' },
			{ value: 'supportPlanner', label: 'Support Planner' }
		]
	},
	{
		label: 'Online & Media Source',
		options: [
			{ value: 'onlineSearch', label: 'Online Search' },
			{ value: 'facebook', label: 'Facebook' },
			{ value: 'instagram', label: 'Instagram' },
			{ value: 'rednote', label: 'RedNote' },
			{ value: 'youtube', label: 'YouTube' },
			{ value: 'wechat', label: 'WeChat Official Account' },
			{ value: 'newspaper', label: 'Newspaper' },
			{ value: 'nextdoor', label: 'Nextdoor' }
		]
	},
	{
		label: 'Other',
		options: [
			{ value: 'event', label: 'Event' },
			{ value: 'na', label: 'N/A' },
			{ value: 'other', label: 'Other-Please Specify' }
		]
	}
]

/** Display labels for Admitted From (single choice); values stored on customer.admitted_from */
export const ADMITTED_FROM_TEXT = {
	newToProgram: 'New to the Program',
	ws1: 'WS1',
	ws2: 'WS2',
	ws3: 'WS3',
	amdcAaPlus: 'AA plus',
	amdcAPlus: 'A plus',
	amdcRoyalGarden: 'Royal Garden',
	amdcCcacc: 'CCACC',
	amdcJarher: 'Jarher',
	amdcRainbow: 'Rainbow',
	amdcArirang: 'Arirang',
	amdcLovingHeart: 'Loving Heart',
	amdcJasmine: 'Jasmine',
	amdcEvergreen: 'Evergreen',
	na: 'N/A',
	other: 'Other – Please Specify',
};

/** Grouped options for react-select (Admission & Discharge — Admitted From) */
export const ADMITTED_FROM_GROUPED = [
	{
		label: ' ',
		options: [{ value: 'newToProgram', label: ADMITTED_FROM_TEXT.newToProgram }],
	},
	{
		label: 'Transfer from Another Worldshine Center',
		options: [
			{ value: 'ws1', label: ADMITTED_FROM_TEXT.ws1 },
			{ value: 'ws2', label: ADMITTED_FROM_TEXT.ws2 },
			{ value: 'ws3', label: ADMITTED_FROM_TEXT.ws3 },
		],
	},
	{
		label: 'Transfer from Another AMDC Center',
		options: [
			{ value: 'amdcAaPlus', label: ADMITTED_FROM_TEXT.amdcAaPlus },
			{ value: 'amdcAPlus', label: ADMITTED_FROM_TEXT.amdcAPlus },
			{ value: 'amdcRoyalGarden', label: ADMITTED_FROM_TEXT.amdcRoyalGarden },
			{ value: 'amdcCcacc', label: ADMITTED_FROM_TEXT.amdcCcacc },
			{ value: 'amdcJarher', label: ADMITTED_FROM_TEXT.amdcJarher },
			{ value: 'amdcRainbow', label: ADMITTED_FROM_TEXT.amdcRainbow },
			{ value: 'amdcArirang', label: ADMITTED_FROM_TEXT.amdcArirang },
			{ value: 'amdcLovingHeart', label: ADMITTED_FROM_TEXT.amdcLovingHeart },
			{ value: 'amdcJasmine', label: ADMITTED_FROM_TEXT.amdcJasmine },
			{ value: 'amdcEvergreen', label: ADMITTED_FROM_TEXT.amdcEvergreen },
		],
	},
	{
		label: ' ',
		options: [
			{ value: 'na', label: ADMITTED_FROM_TEXT.na },
			{ value: 'other', label: ADMITTED_FROM_TEXT.other },
		],
	},
];

/** InterRAI / CFC — Programs & Care Coordination */
export const INTERRAI_CFC_TEXT = {
	interrai: 'InterRAI',
	cfc: 'CFC',
	cpas: 'CPAS',
	conics: 'CONICS',
	other: 'Other – Please Specify',
};

export const INTERRAI_CFC_GROUPED = [
	{
		label: ' ',
		options: [
			{ value: 'interrai', label: INTERRAI_CFC_TEXT.interrai },
			{ value: 'cfc', label: INTERRAI_CFC_TEXT.cfc },
			{ value: 'cpas', label: INTERRAI_CFC_TEXT.cpas },
			{ value: 'conics', label: INTERRAI_CFC_TEXT.conics },
			{ value: 'other', label: INTERRAI_CFC_TEXT.other },
		],
	},
];

/** CMA — Programs & Care Coordination */
export const CMA_TEXT = {
	empoweringMinds: 'Empowering Minds',
	mindKind: 'Mind Kind',
	monumentalHealthSystem: 'Monumental Health System',
	coordinatingCenter: 'The Coordinating Center',
	totalCareServices: 'Total Care Services',
	coWaiver: 'CO Waiver',
	other: 'Other – Please Specify',
};

export const CMA_GROUPED = [
	{
		label: ' ',
		options: [
			{ value: 'empoweringMinds', label: CMA_TEXT.empoweringMinds },
			{ value: 'mindKind', label: CMA_TEXT.mindKind },
			{ value: 'monumentalHealthSystem', label: CMA_TEXT.monumentalHealthSystem },
			{ value: 'coordinatingCenter', label: CMA_TEXT.coordinatingCenter },
			{ value: 'totalCareServices', label: CMA_TEXT.totalCareServices },
			{ value: 'coWaiver', label: CMA_TEXT.coWaiver },
			{ value: 'other', label: CMA_TEXT.other },
		],
	},
];

export const MEAL_TYPE = {
	CHINESE: 'chinese',
	AMERICAN: 'american',
};

export const MEAL_TYPE_TEXT = {
	chinese: 'Chinese',
	american: 'American',
};

export const SOCIAL_BENEFIT_FIELD_KEYS = [
	{ key: 'benefit_ssa', label: 'SSA' },
	{ key: 'benefit_ssi', label: 'SSI' },
	{ key: 'benefit_snap', label: 'SNAP' },
	{ key: 'benefit_hoc', label: 'HOC' },
	{ key: 'benefit_hud', label: 'HUD' },
	{ key: 'benefit_energy_assistance', label: 'Energy Assistance' },
	{ key: 'benefit_cal_r_ride', label: 'Cal-r-Ride' },
	{ key: 'benefit_swimming_pass', label: 'Swimming Pass' },
	{ key: 'benefit_medicaid_statewide_transportation', label: 'Medicaid Statewide Transportation' },
	{ key: 'benefit_lifeline', label: 'Lifeline' },
	{ key: 'benefit_renters_tax_credit', label: "Renter's Tax Credit" },
	{ key: 'benefit_homeowners_tax_credit', label: "Homeowner's Tax Credit" },
	{ key: 'benefit_cpas', label: 'CPAS' },
];

export const CUSTOMER_JOIN_REASON = {
	FRIEND_FAMILY_REFERRAL: 'friendFamilyReferral',
	SOCIAL_WORKER_REFERRAL: 'socialWorkerReferral',
	SOCIAL_MEDIA: 'socialMedia',
	EVENT: 'event',
	OTHER: 'other'
}

export const CUSTOMER_JOIN_REASON_TEXT = {
	friendFamilyReferral: 'Friend or Family Referral',
	socialWorkerReferral: 'Social Worker Referral',
	socialMedia: 'Social Media (Instagram, etc)',
	event: 'Event',
	other: 'Other'
}

export const CUSTOMER_DISCHARGE_REASON = {
	ABSENT_OVER_30: 'absentOver30',
	WS1: 'ws1',
	WS2: 'ws2',
	WS3: 'ws3',
	AMDC_AA_PLUS: 'amdcAaPlus',
	AMDC_A_PLUS: 'amdcAPlus',
	AMDC_ROYAL_GARDEN: 'amdcRoyalGarden',
	AMDC_CCACC: 'amdcCcacc',
	AMDC_JARHER: 'amdcJarher',
	AMDC_RAINBOW: 'amdcRainbow',
	AMDC_ARIRANG: 'amdcArirang',
	AMDC_LOVING_HEART: 'amdcLovingHeart',
	AMDC_JASMINE: 'amdcJasmine',
	AMDC_EVERGREEN: 'amdcEvergreen',
	TRANSFER_OTHER_FACILITY: 'transferOtherFacility',
	DECEASED: 'deceased',
	HOSPITALIZED: 'hospitalized',
	MOVING_OUT_SERVICE_AREA: 'movingOutServiceArea',
	NO_LONGER_ELIGIBLE: 'noLongerEligible',
	NA: 'na',
	OTHER: 'other',
}

export const CUSTOMER_DISCHARGE_REASON_TEXT = {
	absentOver30: 'Absent for Over 30 Days',
	ws1: ADMITTED_FROM_TEXT.ws1,
	ws2: ADMITTED_FROM_TEXT.ws2,
	ws3: ADMITTED_FROM_TEXT.ws3,
	amdcAaPlus: ADMITTED_FROM_TEXT.amdcAaPlus,
	amdcAPlus: ADMITTED_FROM_TEXT.amdcAPlus,
	amdcRoyalGarden: ADMITTED_FROM_TEXT.amdcRoyalGarden,
	amdcCcacc: ADMITTED_FROM_TEXT.amdcCcacc,
	amdcJarher: ADMITTED_FROM_TEXT.amdcJarher,
	amdcRainbow: ADMITTED_FROM_TEXT.amdcRainbow,
	amdcArirang: ADMITTED_FROM_TEXT.amdcArirang,
	amdcLovingHeart: ADMITTED_FROM_TEXT.amdcLovingHeart,
	amdcJasmine: ADMITTED_FROM_TEXT.amdcJasmine,
	amdcEvergreen: ADMITTED_FROM_TEXT.amdcEvergreen,
	transferOtherFacility: 'Transfer to Another Type of Facility',
	deceased: 'Deceased',
	hospitalized: 'Hospitalized',
	movingOutServiceArea: 'Moving out of the Service Area',
	noLongerEligible: 'No Longer Eligible for ADS',
	na: 'N/A',
	other: 'Other-Please Specify',
	// Legacy stored values (no longer offered in dropdown)
	transferWorldshine: 'Transfer to Another Worldshine Center',
	transferAmdc: 'Transfer to Another AMDC Center',
}

/** Grouped react-select options; section labels are headings only (not selectable values). */
export const CUSTOMER_DISCHARGE_REASON_GROUPED = [
	{
		label: ' ',
		options: [{ value: 'absentOver30', label: CUSTOMER_DISCHARGE_REASON_TEXT.absentOver30 }],
	},
	{
		label: 'Transfer to Another Worldshine Center',
		options: [
			{ value: 'ws1', label: ADMITTED_FROM_TEXT.ws1 },
			{ value: 'ws2', label: ADMITTED_FROM_TEXT.ws2 },
			{ value: 'ws3', label: ADMITTED_FROM_TEXT.ws3 },
		],
	},
	{
		label: 'Transfer to Another AMDC Center',
		options: [
			{ value: 'amdcAaPlus', label: ADMITTED_FROM_TEXT.amdcAaPlus },
			{ value: 'amdcAPlus', label: ADMITTED_FROM_TEXT.amdcAPlus },
			{ value: 'amdcRoyalGarden', label: ADMITTED_FROM_TEXT.amdcRoyalGarden },
			{ value: 'amdcCcacc', label: ADMITTED_FROM_TEXT.amdcCcacc },
			{ value: 'amdcJarher', label: ADMITTED_FROM_TEXT.amdcJarher },
			{ value: 'amdcRainbow', label: ADMITTED_FROM_TEXT.amdcRainbow },
			{ value: 'amdcArirang', label: ADMITTED_FROM_TEXT.amdcArirang },
			{ value: 'amdcLovingHeart', label: ADMITTED_FROM_TEXT.amdcLovingHeart },
			{ value: 'amdcJasmine', label: ADMITTED_FROM_TEXT.amdcJasmine },
			{ value: 'amdcEvergreen', label: ADMITTED_FROM_TEXT.amdcEvergreen },
		],
	},
	{
		label: ' ',
		options: [
			{ value: 'transferOtherFacility', label: CUSTOMER_DISCHARGE_REASON_TEXT.transferOtherFacility },
			{ value: 'deceased', label: CUSTOMER_DISCHARGE_REASON_TEXT.deceased },
			{ value: 'hospitalized', label: CUSTOMER_DISCHARGE_REASON_TEXT.hospitalized },
			{ value: 'movingOutServiceArea', label: CUSTOMER_DISCHARGE_REASON_TEXT.movingOutServiceArea },
			{ value: 'noLongerEligible', label: CUSTOMER_DISCHARGE_REASON_TEXT.noLongerEligible },
			{ value: 'na', label: CUSTOMER_DISCHARGE_REASON_TEXT.na },
			{ value: 'other', label: CUSTOMER_DISCHARGE_REASON_TEXT.other },
		],
	},
]

// Diet Preferences (field: dietary_restrictions) - Multi Choice with categories
export const DIETARY_RESTRICTIONS_OPTIONS = [
	// Regular
	{ value: 'regular', label: 'Regular' },
	// Meat
	{ value: 'noRedMeat', label: 'No Red Meat' },
	{ value: 'noPork', label: 'No Pork' },
	{ value: 'noBeef', label: 'No Beef' },
	{ value: 'noLamb', label: 'No Lamb' },
	{ value: 'noChicken', label: 'No Chicken' },
	{ value: 'noTurkey', label: 'No Turkey' },
	{ value: 'noDuck', label: 'No Duck' },
	// Seafood
	{ value: 'noFish', label: 'No Fish' },
	{ value: 'noSeafood', label: 'No Seafood' },
	{ value: 'noShrimp', label: 'No Shrimp' },
	{ value: 'noShellfish', label: 'No Shellfish' },
	// Dairy
	{ value: 'noDairy', label: 'No Dairy' },
	{ value: 'noLactose', label: 'No Lactose' },
	{ value: 'noYoghurt', label: 'No Yoghurt' },
	{ value: 'noBoiledEgg', label: 'No Boiled Egg' },
	{ value: 'noEgg', label: 'No Egg' },
	// Nuts and Snacks
	{ value: 'noNuts', label: 'No Nuts' },
	{ value: 'noPeanuts', label: 'No Peanuts' },
	{ value: 'noCrackers', label: 'No Crackers' },
	// Vegetables and Fruits
	{ value: 'noVegetables', label: 'No Vegetables' },
	{ value: 'noFruits', label: 'No Fruits' },
	{ value: 'noOrange', label: 'No Orange' },
	{ value: 'noApple', label: 'No Apple' },
	{ value: 'noGrapefruit', label: 'No Grapefruit' },
	{ value: 'noGrapes', label: 'No Grapes' },
	{ value: 'noCucumber', label: 'No Cucumber' },
	{ value: 'noCorn', label: 'No Corn' },
	{ value: 'noPickles', label: 'No Pickles' },
	{ value: 'noEggplants', label: 'No Eggplants' },
	{ value: 'noLettuce', label: 'No Lettuce' }
]

// Diet Preferences grouped options for display
export const DIETARY_RESTRICTIONS_GROUPED = [
	{
		label: 'Regular',
		options: [
			{ value: 'regular', label: 'Regular' }
		]
	},
	{
		label: 'Meat',
		options: [
			{ value: 'noRedMeat', label: 'No Red Meat' },
			{ value: 'noPork', label: 'No Pork' },
			{ value: 'noBeef', label: 'No Beef' },
			{ value: 'noLamb', label: 'No Lamb' },
			{ value: 'noChicken', label: 'No Chicken' },
			{ value: 'noTurkey', label: 'No Turkey' },
			{ value: 'noDuck', label: 'No Duck' }
		]
	},
	{
		label: 'Seafood',
		options: [
			{ value: 'noFish', label: 'No Fish' },
			{ value: 'noSeafood', label: 'No Seafood' },
			{ value: 'noShrimp', label: 'No Shrimp' },
			{ value: 'noShellfish', label: 'No Shellfish' }
		]
	},
	{
		label: 'Dairy',
		options: [
			{ value: 'noDairy', label: 'No Dairy' },
			{ value: 'noLactose', label: 'No Lactose' },
			{ value: 'noYoghurt', label: 'No Yoghurt' },
			{ value: 'noBoiledEgg', label: 'No Boiled Egg' },
			{ value: 'noEgg', label: 'No Egg' }
		]
	},
	{
		label: 'Nuts and Snacks',
		options: [
			{ value: 'noNuts', label: 'No Nuts' },
			{ value: 'noPeanuts', label: 'No Peanuts' },
			{ value: 'noCrackers', label: 'No Crackers' }
		]
	},
	{
		label: 'Vegetables and Fruits',
		options: [
			{ value: 'noVegetables', label: 'No Vegetables' },
			{ value: 'noFruits', label: 'No Fruits' },
			{ value: 'noOrange', label: 'No Orange' },
			{ value: 'noApple', label: 'No Apple' },
			{ value: 'noGrapefruit', label: 'No Grapefruit' },
			{ value: 'noGrapes', label: 'No Grapes' },
			{ value: 'noCucumber', label: 'No Cucumber' },
			{ value: 'noCorn', label: 'No Corn' },
			{ value: 'noPickles', label: 'No Pickles' },
			{ value: 'noEggplants', label: 'No Eggplants' },
			{ value: 'noLettuce', label: 'No Lettuce' }
		]
	}
]

export const DIET_TEXTURE = {
	REGULAR: 'regular',
	CHOPPED: 'chopped',
	PUREED: 'pureed',
	LIQUID: 'liquid'
}

export const DIET_TEXTURE_TEXT = {
	regular: 'Regular',
	chopped: 'Chopped',
	pureed: 'Pureed',
	liquid: 'Liquid'
}

export const TRANSPORTATION_TYPE = {
	ROUND_TRIP: 'roundTrip',
	PICKUP_ONLY: 'pickupOnly',
	DROPOFF_ONLY: 'dropoffOnly',
	SELF_TRANSPORT: 'selfTransport'
}

export const TRANSPORTATION_TYPE_TEXT = {
	roundTrip: 'Round Trip',
	pickupOnly: 'Pick-up Only',
	dropoffOnly: 'Drop-off Only',
	selfTransport: 'Self-Transport'
}

export const YES_NO = {
	YES: 'yes',
	NO: 'no'
}

export const YES_NO_TEXT = {
	yes: 'Yes',
	no: 'No'
}

export const PRE_SCREENING_FORM = {
	YES: 'yes',
	NO: 'no',
	NO_NEED: 'no_need'
}

export const PRE_SCREENING_FORM_TEXT = {
	yes: 'Yes',
	no: 'No',
	no_need: 'No Need'
}

export const PREFERRED_TEXT_LANGUAGE = {
	ENGLISH: 'english',
	CHINESE: 'chinese'
}

export const PREFERRED_TEXT_LANGUAGE_TEXT = {
	english: 'English',
	chinese: 'Chinese'
}
